import React, { useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { ApiLoadState } from './ApiLoadState';
import { useAuth } from '../../context/AuthContext';
import { useGetDailySaleByIdQuery } from '../../redux/dailySalesApi';
import {
  formatSaleAmount,
  formatSaleDate,
  formatSaleDateTime,
  mapDailySaleRow,
  mergeDailySaleDetails,
  unwrapDailySale,
} from '../../utils/dailySalesList';

const LIST_PATH = '/dashboard/admin/build-to-flow/daily-sales';

const ITEM_COLUMNS = [
  'Product',
  'Bill Type',
  'Bill No',
  'Item Code',
  'Qty Sold',
  'Unit',
  'Rate',
  'Gross Amount',
  'Discount',
  'Tax Rate',
  'Tax Amount',
  'Amount',
];

function displayValue(value) {
  if (value == null || value === '') return '—';
  return value;
}

function DetailRow({ label, value, accent }) {
  return (
    <div className="grid gap-1 border-b border-gray-100 py-3 sm:grid-cols-2 sm:gap-8">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-sm font-medium ${accent ? 'text-brand' : 'text-gray-900'}`}>
        {displayValue(value)}
      </p>
    </div>
  );
}

function AdminDailySalesView() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const { id } = useParams();
  const location = useLocation();
  const previewSale = location.state?.sale;
  const { data, isLoading, isFetching, error, refetch } = useGetDailySaleByIdQuery(id, {
    skip: !id,
  });

  const sale = useMemo(() => {
    const unwrapped = unwrapDailySale(data);
    const fromApi = unwrapped ? mapDailySaleRow(unwrapped) : null;
    return mergeDailySaleDetails(fromApi, previewSale || null);
  }, [data, previewSale]);

  const loadFailed = Boolean(error && !sale);
  return (
    <DashboardLayout title="Daily Sale Details">
      <div className="space-y-4 py-4">
        <div className="flex justify-end">
          <p className="text-sm text-gray-500">
            <Link
              to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
              className="text-brand hover:underline"
            >
              Home
            </Link>
            {' / '}
            <Link to={LIST_PATH} className="text-brand hover:underline">
              Daily Sales
            </Link>
            {' / '}
            <span className="text-gray-400">View</span>
          </p>
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          {loadFailed ? (
            <ApiLoadState
              isLoading={isLoading}
              isFetching={isFetching}
              hasData={false}
              error={error}
              refetch={refetch}
              message="Loading daily sale…"
              errorMessage={error?.data?.message || 'Unable to load daily sale details.'}
              className="py-10"
            />
          ) : isLoading && !sale ? (
            <ApiLoadState
              isLoading
              isFetching={isFetching}
              hasData={false}
              message="Loading daily sale…"
              className="py-10"
            />
          ) : !sale ? (
            <p className="py-10 text-center text-sm text-gray-500">Daily sale not found.</p>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Daily Sale Details</h2>
                <Link
                  to={`${LIST_PATH}/${id}/edit`}
                  state={{ sale }}
                  className="inline-flex items-center justify-center rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-800"
                >
                  Edit
                </Link>
              </div>
              <div className="mt-4 max-w-3xl">
                <DetailRow label="Reference Number" value={sale.referenceNumber} />
                <DetailRow label="Bill Date" value={formatSaleDate(sale.billDate)} />
                <DetailRow label="Location" value={sale.location} accent />
                <DetailRow label="Guest Count" value={sale.guestCount} />
                <DetailRow label="Total Amount" value={formatSaleAmount(sale.amount)} />
                <DetailRow label="Created At" value={formatSaleDateTime(sale.createdAt)} />
                <DetailRow label="Updated At" value={formatSaleDateTime(sale.updatedAt)} />
              </div>

              <h3 className="mt-8 text-base font-semibold text-gray-900">
                Sale Items ({sale.items?.length || 0})
              </h3>
              {!sale.items?.length ? (
                <p className="mt-3 text-sm text-gray-500">No sale items found.</p>
              ) : (
                <>
                  <ul className="mt-3 space-y-3 lg:hidden">
                    {sale.items.map((item, index) => (
                      <li
                        key={item.id || `${item.itemCode}-${index}`}
                        className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                      >
                        <p className="text-sm font-semibold text-gray-900">{item.productLabel}</p>

                        <p className="mt-1 text-xs text-gray-600">Code: {item.itemCode}</p>
                        <p className="mt-1 text-xs text-gray-600">
                          Qty: {item.qtySold} · {item.unit} · Rate: {item.rate}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">Amount: {item.amount}</p>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 hidden overflow-x-auto lg:block">
                    <table className="min-w-full border-collapse text-sm">
                      <thead>
                        <tr>
                          {ITEM_COLUMNS.map((label) => (
                            <th
                              key={label}
                              className="whitespace-nowrap border-b border-gray-200 bg-[#F7E5D5] px-3 py-2 text-left font-semibold text-gray-700"
                            >
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sale.items.map((item, index) => (
                          <tr key={item.id || `${item.itemCode}-${index}`} className="hover:bg-amber-50/40">
                            <td className="border-b border-gray-100 px-3 py-2 font-medium text-brand">
                              {item.productLabel}
                            </td>
                             <td className="border-b border-gray-100 px-3 py-2">{item.billType}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.billNo}</td>
                            <td className="border-b border-gray-100 px-3 py-2 text-brand">{item.itemCode}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.qtySold}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.unit}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.rate}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.grossAmount}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.discount}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.taxRate}</td>
                            <td className="border-b border-gray-100 px-3 py-2">{item.taxAmount}</td>
                            <td className="border-b border-gray-100 px-3 py-2 font-medium">{item.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminDailySalesView;
